import { useRef, useState } from 'react';
import {
    IconAlignCenter,
    IconAlignJustified,
    IconAlignLeft,
    IconAlignRight,
    IconArrowDown,
    IconArrowUp,
    IconBold,
    IconColumnInsertLeft,
    IconColumnInsertRight,
    IconColumnRemove,
    IconItalic,
    IconLink,
    IconLock,
    IconLockOpen,
    IconPalette,
    IconRowInsertBottom,
    IconRowInsertTop,
    IconRowRemove,
    IconTrash,
    IconUnderline,
} from '@tabler/icons-react';
import {
    COLOR_SWATCHES,
    FONT_SIZES,
    HIGHLIGHT_SWATCHES,
    OBJ,
    TOOLS,
} from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { isAspectLocked } from '../engine/objects';
import { addRow, removeRow, addCol, removeCol } from '../engine/tableLayout';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import Hint from '../../../components/common/Hint';
import FontPicker from './FontPicker';
import { useFormatting } from './useFormatting';
import {
    ColorGrid,
    Divider,
    EDITOR_CHROME_ATTR,
    LabeledRow,
    MiniSelect,
    Popover,
    Segmented,
    Slider,
    keepTextEditorFocus,
    useCompactLayout,
} from '../ui/prims';

/** Small colour button that opens a swatch popover. */
function ColorField({ label, value, onChange, swatches = COLOR_SWATCHES, allowTransparent }) {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    return (
        <>
            <Hint label={label}>
                <button
                    ref={ref}
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={label}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2 dark:border-zinc-700 dark:bg-zinc-900"
                >
                    <span
                        className="relative h-4 w-4 overflow-hidden rounded border border-[#ECE8E4] dark:border-zinc-600"
                        style={{ background: value === 'transparent' ? '#fff' : value }}
                    >
                        {value === 'transparent' ? (
                            <span className="absolute left-1/2 top-1/2 h-[1.5px] w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#D33A2C]" />
                        ) : null}
                    </span>
                    <IconPalette size={13} stroke={1.8} className="text-[#A8A099]" />
                </button>
            </Hint>
            <Popover anchorRef={ref} open={open} onClose={() => setOpen(false)} width={214}>
                <LabeledRow label={label}>
                    <ColorGrid
                        colors={swatches}
                        value={value}
                        onChange={(c) => { onChange(c); setOpen(false); }}
                        allowTransparent={allowTransparent}
                    />
                </LabeledRow>
                <input
                    type="color"
                    value={value === 'transparent' ? '#ffffff' : value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-8 w-full cursor-pointer rounded-lg border border-[#ECE8E4] bg-white dark:border-zinc-700 dark:bg-zinc-900"
                />
            </Popover>
        </>
    );
}

function Toggle({ icon: Icon, label, active, onClick }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                aria-label={label}
                aria-pressed={active}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
                    active
                        ? 'border-primary bg-primary text-white'
                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                }`}
            >
                <Icon size={16} stroke={1.9} />
            </button>
        </Hint>
    );
}

/**
 * The properties of the selected object, or the defaults of the armed tool when
 * nothing is selected.
 *
 * `stacked` turns the row into a column for the inspector panel, which also
 * takes over the reorder and delete buttons; everything else — which controls
 * appear for what, and whether a change writes to a run of text, the whole box
 * or the tool's defaults — is the same either way.
 */
export default function OptionsBar({ stacked = false }) {
    const { t } = useTranslation();
    const { activeTool, toolSettings, object, uid, write, textish, textSource } = useFormatting();
    // `write` routes text through the run/box decision; these two are for the
    // branches that always mean the object itself, or the tool's defaults.
    const updateObject = usePdfEditorStore((s) => s.updateObject);
    const editingId = usePdfEditorStore((s) => s.editingId);
    const editingCell = usePdfEditorStore((s) => s.editingCell);
    const setEditing = usePdfEditorStore((s) => s.setEditing);
    const documentMode = usePdfEditorStore((s) => s.documentMode);
    const updateToolSettings = usePdfEditorStore((s) => s.updateToolSettings);
    const removeObject = usePdfEditorStore((s) => s.removeObject);
    const reorderObject = usePdfEditorStore((s) => s.reorderObject);
    const setLinkDraft = usePdfEditorStore((s) => s.setLinkDraft);
    const compact = useCompactLayout();

    const rows = [];

    if (textish) {
        const source = textSource;
        // A calibrated box lands on sizes like 11.6 that are not in the preset
        // list; without adding it the <select> falls back to its first option
        // and reports the wrong size.
        const sizes = FONT_SIZES.includes(source.fontSize)
            ? FONT_SIZES
            : [...FONT_SIZES, source.fontSize].sort((a, b) => a - b);
        rows.push(
            // The whole text row is one group, so it has to wrap internally
            // too — otherwise it is the group, not the bar, that overflows.
            <div key="font" className={`flex items-center gap-1.5 ${compact ? 'flex-wrap' : ''}`}>
                <div className="w-[152px]">
                    <FontPicker
                        value={source.fontFamily}
                        bold={source.bold}
                        italic={source.italic}
                        // A box made by "Edit text" is set in the PDF's own
                        // embedded face, which is in no catalogue — name it, so
                        // the picker says what is actually on the page.
                        label={source.fontStack
                            ? (source.fontLabel || t('pdfEditor.options.documentFont'))
                            : source.fontFamily}
                        onChange={(v) => write('text', { fontFamily: v, fontStack: null, fontLabel: null })}
                    />
                </div>
                <div className="w-[72px]">
                    <MiniSelect
                        value={String(source.fontSize)}
                        onChange={(v) => write('text', { fontSize: Number(v) })}
                        options={sizes.map((s) => ({ value: String(s), label: String(s) }))}
                    />
                </div>
                <Toggle
                    icon={IconBold}
                    label={t('pdfEditor.options.bold')}
                    active={!!source.bold}
                    onClick={() => write('text', { bold: !source.bold })}
                />
                <Toggle
                    icon={IconItalic}
                    label={t('pdfEditor.options.italic')}
                    active={!!source.italic}
                    onClick={() => write('text', { italic: !source.italic })}
                />
                <Toggle
                    icon={IconUnderline}
                    label={t('pdfEditor.options.underline')}
                    active={!!source.underline}
                    onClick={() => write('text', { underline: !source.underline })}
                />
                <Segmented
                    value={source.align || 'left'}
                    onChange={(v) => write('text', { align: v })}
                    options={[
                        { value: 'left', icon: IconAlignLeft, iconOnly: true, title: t('pdfEditor.options.alignLeft'), hint: t('pdfEditor.options.alignHint') },
                        { value: 'center', icon: IconAlignCenter, iconOnly: true, title: t('pdfEditor.options.alignCenter') },
                        { value: 'right', icon: IconAlignRight, iconOnly: true, title: t('pdfEditor.options.alignRight') },
                        { value: 'justify', icon: IconAlignJustified, iconOnly: true, title: t('pdfEditor.options.alignJustify') },
                    ]}
                />
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={source.color}
                    onChange={(c) => write('text', { color: c })}
                />
            </div>,
        );
    }

    const inkTool = !object && (activeTool === TOOLS.PENCIL || activeTool === TOOLS.HIGHLIGHT);
    if (inkTool || object?.type === OBJ.INK) {
        const highlight = object ? object.mode === 'highlight' : activeTool === TOOLS.HIGHLIGHT;
        const key = highlight ? 'highlight' : 'pencil';
        const source = object || toolSettings[key];
        rows.push(
            <div key="ink" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={source.color}
                    onChange={(c) => write(key, { color: c })}
                    swatches={highlight ? HIGHLIGHT_SWATCHES : COLOR_SWATCHES}
                />
                <div className="w-[150px]">
                    <Slider
                        value={source.strokeWidth}
                        min={highlight ? 4 : 0.5}
                        max={highlight ? 40 : 16}
                        step={0.5}
                        onChange={(v) => write(key, { strokeWidth: v })}
                        suffix="pt"
                    />
                </div>
            </div>,
        );
    }

    const shapeish = object && [OBJ.RECT, OBJ.ELLIPSE, OBJ.LINE, OBJ.ARROW].includes(object.type);
    if (shapeish || (!object && activeTool === TOOLS.SHAPE)) {
        const source = object || toolSettings.shape;
        const closed = !object || [OBJ.RECT, OBJ.ELLIPSE].includes(object.type);
        rows.push(
            <div key="shape" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.stroke')}
                    value={source.stroke}
                    onChange={(c) => write('shape', { stroke: c })}
                />
                {closed ? (
                    <ColorField
                        label={t('pdfEditor.options.fill')}
                        value={source.fill}
                        onChange={(c) => write('shape', { fill: c })}
                        allowTransparent
                    />
                ) : null}
                <div className="w-[140px]">
                    <Slider
                        value={source.strokeWidth}
                        min={0}
                        max={14}
                        step={0.5}
                        onChange={(v) => write('shape', { strokeWidth: v })}
                        suffix="pt"
                    />
                </div>
            </div>,
        );
    }

    // Underline / strike-out draw a line object, so once one is placed the
    // shape row above edits it; this row is for the armed tool's defaults.
    // UNDERLINE/STRIKEOUT act on a real PDF's text layer — hidden in document mode.
    const decorTool = !documentMode && !object && (activeTool === TOOLS.UNDERLINE || activeTool === TOOLS.STRIKEOUT);
    if (decorTool) {
        const key = activeTool === TOOLS.UNDERLINE ? 'underline' : 'strikeout';
        const source = toolSettings[key];
        rows.push(
            <div key="decor" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={source.color}
                    onChange={(c) => updateToolSettings(key, { color: c })}
                />
                <div className="w-[170px] flex-none">
                    <Slider
                        value={source.strokeWidth}
                        min={0.5}
                        max={6}
                        step={0.5}
                        onChange={(v) => updateToolSettings(key, { strokeWidth: v })}
                        suffix="pt"
                    />
                </div>
                <span className="whitespace-nowrap text-[11.5px] text-[#6B635C] dark:text-zinc-400">
                    {t(`pdfEditor.options.${key}Hint`)}
                </span>
            </div>,
        );
    }

    // A white-out is made by "Edit text" (the patch that hides the old words), and
    // its colour can be corrected when one is selected.
    if (!documentMode && object?.type === OBJ.WHITEOUT) {
        rows.push(
            <div key="fill" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.fill')}
                    value={object.fill}
                    onChange={(c) => write('whiteout', { fill: c })}
                />
                <span className="text-[11.5px] text-[#6B635C] dark:text-zinc-400">
                    {t('pdfEditor.options.eraserHint')}
                </span>
            </div>,
        );
    }

    const stampish = object?.type === OBJ.CHECK || object?.type === OBJ.CROSS
        || (!object && (activeTool === TOOLS.CHECK || activeTool === TOOLS.CROSS));
    if (stampish) {
        const key = (object?.type === OBJ.CROSS || activeTool === TOOLS.CROSS) ? 'cross' : 'check';
        const source = object || toolSettings[key];
        rows.push(
            <div key="stamp" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={source.color}
                    onChange={(c) => write(key, { color: c })}
                />
                <div className="w-[130px]">
                    <Slider
                        value={source.strokeWidth}
                        min={1}
                        max={10}
                        step={0.5}
                        onChange={(v) => write(key, { strokeWidth: v })}
                        suffix="pt"
                    />
                </div>
            </div>,
        );
    }

    if (object?.type === OBJ.NOTE || (!object && activeTool === TOOLS.NOTE)) {
        const source = object || toolSettings.note;
        rows.push(
            <div key="note" className="flex items-center gap-2">
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={source.color}
                    onChange={(c) => write('note', { color: c })}
                    swatches={HIGHLIGHT_SWATCHES}
                />
            </div>,
        );
    }

    if (object?.type === OBJ.TABLE) {
        // Rows and columns go in and come out AT THE CELL BEING TYPED IN — above or
        // below it, left or right of it — as in Word and Google Docs; with no cell
        // open, at the end of the table.
        const cell = editingId === object.id && editingCell ? editingCell : null;
        const r = cell ? cell.r : object.rows - 1;
        const c = cell ? cell.c : object.cols - 1;
        // Pass the new structure; the store recomputes row heights + total height.
        const apply = (next, moveTo) => {
            updateObject(uid, object.id, {
                rows: next.rows, cols: next.cols, cells: next.cells, w: next.w,
                colFracs: next.colFracs, merges: next.merges, cellStyles: next.cellStyles, rowMin: next.rowMin,
            });
            // Keep typing in the same cell, wherever it moved to; a removed one closes.
            if (cell) setEditing(moveTo ? object.id : null, moveTo || null, moveTo ? { offset: String(next.cells?.[moveTo.r]?.[moveTo.c] ?? '').length } : null);
        };
        const tableButton = (Icon, label, onClick, disabled = false) => (
            <Hint key={label} label={label}>
                <button
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] px-2 text-[12px] font-medium text-[#3D3833] transition-colors hover:border-primary/50 hover:text-primary disabled:pointer-events-none disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-200"
                >
                    <Icon size={16} stroke={1.8} />
                    <span className="truncate">{label}</span>
                </button>
            </Hint>
        );
        rows.push(
            <div key="table" className="grid grid-cols-2 gap-1.5">
                {tableButton(IconRowInsertTop, t('pdfEditor.table.rowAbove'), () => apply(addRow(object, r), cell && { r: cell.r + 1, c: cell.c }))}
                {tableButton(IconRowInsertBottom, t('pdfEditor.table.rowBelow'), () => apply(addRow(object, r + 1), cell))}
                {tableButton(IconColumnInsertLeft, t('pdfEditor.table.colLeft'), () => apply(addCol(object, c), cell && { r: cell.r, c: cell.c + 1 }))}
                {tableButton(IconColumnInsertRight, t('pdfEditor.table.colRight'), () => apply(addCol(object, c + 1), cell))}
                {tableButton(IconRowRemove, t('pdfEditor.table.deleteRow'), () => apply(removeRow(object, r), null), object.rows <= 1)}
                {tableButton(IconColumnRemove, t('pdfEditor.table.deleteCol'), () => apply(removeCol(object, c), null), object.cols <= 1)}
            </div>,
            <div key="table-border" className="flex items-center gap-2">
                <span className="text-[12px] text-[#6B635C] dark:text-zinc-400">{t('pdfEditor.table.border')}</span>
                <ColorField
                    label={t('pdfEditor.table.border')}
                    value={object.borderColor}
                    onChange={(color) => updateObject(uid, object.id, { borderColor: color })}
                />
            </div>,
        );
    }

    if (object?.type === OBJ.LINK) {
        const target = object.url
            ? object.url
            : Number.isInteger(object.targetPage)
                ? t('pdfEditor.link.toPage', { number: toBnDigits(object.targetPage + 1) })
                : null;
        rows.push(
            <div key="link" className="flex items-center gap-2">
                <Hint label={target || undefined}>
                    <span
                        className={`max-w-[320px] truncate text-[12.5px] ${
                            target ? 'text-[#3D3833] dark:text-zinc-200' : 'font-medium text-[#D33A2C]'
                        }`}
                    >
                        {target || t('pdfEditor.link.none')}
                    </span>
                </Hint>
                <button
                    type="button"
                    onClick={() => setLinkDraft({ uid, id: object.id, isNew: false })}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] px-2.5 text-[12.5px] font-medium text-[#3D3833] hover:border-primary/50 dark:border-zinc-700 dark:text-zinc-200"
                >
                    <IconLink size={14} stroke={1.8} />
                    {t('pdfEditor.link.edit')}
                </button>
                <ColorField
                    label={t('pdfEditor.options.color')}
                    value={object.color}
                    onChange={(c) => updateObject(uid, object.id, { color: c })}
                />
            </div>,
        );
    }

    if (object?.type === OBJ.IMAGE) {
        const locked = isAspectLocked(object);
        rows.push(
            <div key="image" className="flex items-center gap-2">
                <span className="text-[11.5px] font-medium text-[#6B635C] dark:text-zinc-400">
                    {t('pdfEditor.options.opacity')}
                </span>
                <div className="w-[150px]">
                    <Slider
                        value={Math.round((object.opacity ?? 1) * 100)}
                        min={10}
                        max={100}
                        onChange={(v) => updateObject(uid, object.id, { opacity: v / 100 }, { transient: true })}
                        suffix="%"
                    />
                </div>
                <Hint label={t('pdfEditor.options.lockAspect')}>
                    <button
                        type="button"
                        onClick={() => updateObject(uid, object.id, { lockAspect: !locked })}
                        aria-pressed={locked}
                        className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium transition-colors ${
                            locked
                                ? 'border-primary bg-primary text-white'
                                : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                        }`}
                    >
                        {locked ? <IconLock size={14} stroke={1.9} /> : <IconLockOpen size={14} stroke={1.9} />}
                        {t('pdfEditor.options.lockAspect')}
                    </button>
                </Hint>
            </div>,
        );
    }

    // On a phone the hint would cost a whole row of screen to say "pick a tool".
    // The panel says it in its own empty state, so it is not repeated here.
    if (!rows.length && !object && !compact && !stacked) {
        rows.push(
            <span key="hint" className="text-[12px] text-[#A8A099] dark:text-zinc-500">
                {t('pdfEditor.options.idleHint')}
            </span>,
        );
    }
    if (!rows.length && (compact || stacked)) return null;

    return (
        <div
            {...{ [EDITOR_CHROME_ATTR]: '' }}
            // Clicking a control must not pull the caret out of an open text
            // box: the edit would end under the click, and with an empty box it
            // would take this whole row with it.
            onMouseDown={keepTextEditorFocus}
            className={stacked
                // In the panel each row is its own line and wraps inside itself,
                // so nothing is ever off the side of a 266px column.
                ? 'flex flex-col items-stretch gap-2.5 [&>div]:flex-wrap [&>div]:gap-1.5'
                : `flex flex-none items-center gap-2 border-b border-[#ECE8E4] bg-[#FBFAF8] px-3 dark:border-zinc-800 dark:bg-zinc-900/60 ${
                    // On a phone the controls wrap onto a second row instead of
                    // running off the side where they cannot be reached.
                    compact ? 'min-h-[46px] flex-wrap py-1.5' : 'h-[46px] overflow-x-auto [scrollbar-width:thin]'
                }`}
        >
            {rows}

            {object && !stacked ? (
                <>
                    <Divider />
                    <Hint label={t('pdfEditor.options.bringFront')}>
                        <button
                            type="button"
                            onClick={() => reorderObject(uid, object.id, 'front')}
                            aria-label={t('pdfEditor.options.bringFront')}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconArrowUp size={16} stroke={1.8} />
                        </button>
                    </Hint>
                    <Hint label={t('pdfEditor.options.sendBack')}>
                        <button
                            type="button"
                            onClick={() => reorderObject(uid, object.id, 'back')}
                            aria-label={t('pdfEditor.options.sendBack')}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconArrowDown size={16} stroke={1.8} />
                        </button>
                    </Hint>
                    <Hint label={t('common.delete')}>
                        <button
                            type="button"
                            onClick={() => removeObject(uid, object.id)}
                            aria-label={t('common.delete')}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ECE8E4] bg-white text-[#D33A2C] hover:border-[#D33A2C]/50 dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <IconTrash size={16} stroke={1.8} />
                        </button>
                    </Hint>
                </>
            ) : null}
        </div>
    );
}
